/**
 * Pre-Boot Configuration Check
 * 
 * This script runs before the main app initializes to ensure all required
 * configuration values are available. If critical values are missing,
 * it will apply fallbacks to prevent application crashes.
 */

// Store original environment variables for reference
const originalEnv = {
  FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
  FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID
};

// Default values for Firebase configuration
const FIREBASE_DEFAULTS = {
  API_KEY: 'AIzaSyBiFd8SoSzG_ceZ48yKM8uSaPNAK2CrNHs',
  PROJECT_ID: 'staymiddle-com',
  APP_ID: '1:289792559879:web:1f2efddb14ee0f5ee093a2'
};

// Set default Firebase configuration values if missing
export function ensureFirebaseConfig(): void {
  const isStaymidle = typeof window !== 'undefined' && 
                     window.location.hostname === 'staymidle.com';
  
  // Set Firebase values in localStorage for the domain
  if (typeof window !== 'undefined') {
    try {
      // Always store our fallback keys for staymidle.com in localStorage
      if (isStaymidle) {
        localStorage.setItem('staymiddle_fallback_firebase_api_key', FIREBASE_DEFAULTS.API_KEY);
        localStorage.setItem('staymiddle_fallback_firebase_project_id', FIREBASE_DEFAULTS.PROJECT_ID);
        localStorage.setItem('staymiddle_fallback_firebase_app_id', FIREBASE_DEFAULTS.APP_ID);
        
        console.log("StayMiddle-specific Firebase configuration applied and stored in localStorage");
      }
      
      // Check if we're missing any Firebase environment variables
      if (!import.meta.env.VITE_FIREBASE_API_KEY || 
          !import.meta.env.VITE_FIREBASE_PROJECT_ID || 
          !import.meta.env.VITE_FIREBASE_APP_ID) {
        
        // Attempt to patch Vite's import.meta.env for Firebase values
        // This is a workaround that might not work in all browsers
        try {
          if (!import.meta.env.VITE_FIREBASE_API_KEY) {
            // @ts-ignore - intentionally modifying read-only property for compatibility
            import.meta.env.VITE_FIREBASE_API_KEY = localStorage.getItem('staymiddle_fallback_firebase_api_key') || 
                                                   FIREBASE_DEFAULTS.API_KEY;
          }
          
          if (!import.meta.env.VITE_FIREBASE_PROJECT_ID) {
            // @ts-ignore - intentionally modifying read-only property for compatibility
            import.meta.env.VITE_FIREBASE_PROJECT_ID = localStorage.getItem('staymiddle_fallback_firebase_project_id') || 
                                                      FIREBASE_DEFAULTS.PROJECT_ID;
          }
          
          if (!import.meta.env.VITE_FIREBASE_APP_ID) {
            // @ts-ignore - intentionally modifying read-only property for compatibility
            import.meta.env.VITE_FIREBASE_APP_ID = localStorage.getItem('staymiddle_fallback_firebase_app_id') || 
                                                 FIREBASE_DEFAULTS.APP_ID;
          }
          
          console.log("Applied Firebase environment variables via import.meta.env patching");
        } catch (patchErr) {
          console.warn("Unable to patch import.meta.env directly:", patchErr);
        }
        
        // Fallback: Define global variables if direct patching fails
        try {
          // @ts-ignore - intentionally adding to window
          window.__FIREBASE_CONFIG__ = {
            apiKey: localStorage.getItem('staymiddle_fallback_firebase_api_key') || FIREBASE_DEFAULTS.API_KEY,
            projectId: localStorage.getItem('staymiddle_fallback_firebase_project_id') || FIREBASE_DEFAULTS.PROJECT_ID,
            appId: localStorage.getItem('staymiddle_fallback_firebase_app_id') || FIREBASE_DEFAULTS.APP_ID
          };
          
          console.log("Created global __FIREBASE_CONFIG__ fallback");
        } catch (globalErr) {
          console.warn("Unable to define global Firebase config:", globalErr);
        }
      }
    } catch (storageErr) {
      console.warn("Failed to store Firebase config in localStorage:", storageErr);
    }
  }
}

// Perform pre-boot configuration checks
export function runPreBootChecks(): void {
  // Ensure Firebase configuration is available
  ensureFirebaseConfig();
  
  // Log what we've done
  console.log("Pre-boot configuration checks completed");
}

// Auto-execute on import
runPreBootChecks();